import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';

interface MonthDayPickerProps {
  value: string; // MM-DD 格式
  onChange: (value: string) => void;
  placeholder?: string;
}

export function MonthDayPicker({ value, onChange, placeholder = 'MM-DD' }: MonthDayPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return now.getMonth(); // 0-11
  });
  const [currentYear] = useState(() => new Date().getFullYear());
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupStyle, setPopupStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && popupRef.current &&
          !containerRef.current.contains(event.target as Node) &&
          !popupRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const updatePopupPosition = () => {
    if (containerRef.current && popupRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popupWidth = 288; // w-72 = 18rem = 288px
      const popupHeight = popupRef.current.offsetHeight;

      // 計算水平位置，確保不超出屏幕右側
      let left = rect.left + (rect.width - popupWidth) / 2;
      if (left + popupWidth > window.innerWidth) {
        left = window.innerWidth - popupWidth - 16; // 16px 右側邊距
      }
      if (left < 16) {
        left = 16; // 16px 左側邊距
      }

      // 計算垂直位置，確保不超出屏幕底部
      let top = rect.bottom + 8;
      if (top + popupHeight > window.innerHeight) {
        top = rect.top - popupHeight - 8; // 顯示在輸入框上方
      }

      setPopupStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePopupPosition();
      const handleResize = () => updatePopupPosition();
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize, true);
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize, true);
      };
    }
  }, [isOpen]);

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDayClick = (day: number) => {
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    onChange(`${month}-${dayStr}`);
    setIsOpen(false);
  };

  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDayOfMonth }, () => null);

  const weekDays = ['日', '一', '二', '三', '四', '五', '六'];

  const popupContent = (
    <div ref={popupRef} style={popupStyle as React.CSSProperties} className="bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-72">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => setCurrentMonth(prev => prev === 0 ? 11 : prev - 1)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="text-center font-semibold">
          {monthNames[currentMonth]}
        </div>
        <button
          type="button"
          onClick={() => setCurrentMonth(prev => prev === 11 ? 0 : prev + 1)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {emptyDays.map((_, i) => (
          <div key={`empty-${i}`} className="h-8" />
        ))}
        {days.map(day => {
          const isSelected = value === `${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          return (
            <button
              key={day}
              type="button"
              onClick={() => handleDayClick(day)}
              className={`h-8 rounded text-sm font-medium transition-colors ${
                isSelected
                  ? 'bg-blue-500 text-white'
                  : 'hover:bg-gray-100 text-gray-900'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setIsOpen(false)}
          className="text-xs"
        >
          確定
        </Button>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={() => {}}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        readOnly
      />
      {isOpen && createPortal(popupContent, document.body)}
    </div>
  );
}
